import { ref, watch, computed, effectScope, nextTick } from 'vue'
import { watchDebounced } from '@vueuse/core'
import { useSystemStore, useUserParameterStore, useAuthStore } from '@/stores'
import type { UserParameterType } from '@/types/types'

const THEME_MAP = { Claro: 0, Escuro: 1 } as const
const SYNC_INTERVAL_MS = 60 * 60 * 1000
const STORAGE_KEY = 'theme_sync_state'
const PREFS_DEBOUNCE_MS = 500

type ThemeString = 'Claro' | 'Escuro'

const themeUtils = {
  /** 0=Claro, 1=Escuro, 2=Sistema */
  toString(val: number): ThemeString {
    if (val === 1) return 'Escuro'
    if (val === 2) return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'Escuro' : 'Claro'
    return 'Claro'
  },
  toValue(str: string): number {
    return str === 'Escuro' ? 1 : 0
  }
}

interface PersistedState {
  userId: string
  tenantId: string
  id: string
  lastUpdate: number | null
  lastSyncTimestamp: number | null
  dark: number
  lang: string
  volume: number
  autoplay: boolean
  hasPendingChanges: boolean
}

function createPersistence(
  getUserId: () => string | null,
  getTenantId: () => string
) {
  const isStale = (data: PersistedState) =>
    data.userId !== getUserId() || data.tenantId !== getTenantId()

  return {
    load(): PersistedState | null {
      try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return null
        const data: PersistedState = JSON.parse(raw)
        if (isStale(data)) {
          this.clear()
          return null
        }
        return data
      } catch {
        return null
      }
    },

    save(state: Omit<PersistedState, 'userId' | 'tenantId'>): void {
      const userId = getUserId()
      if (!userId) return
      const entry: PersistedState = { userId, tenantId: getTenantId(), ...state }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(entry))
    },

    clear(): void {
      localStorage.removeItem(STORAGE_KEY)
    }
  }
}

/** Suprime o watcher de prefs durante mudanças programáticas. */
function createWatcherSuppressor() {
  let suppressed = false
  let timer: ReturnType<typeof setTimeout> | null = null

  return {
    suppress() {
      suppressed = true
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        suppressed = false
        timer = null
      }, PREFS_DEBOUNCE_MS + 50)
    },
    isSuppressed: () => suppressed
  }
}

const isSyncing = ref(false)
const isInitialized = ref(false)
const hasPendingChanges = ref(false)
const themeOverrideActive = ref(false)

let syncTimer: ReturnType<typeof setInterval> | null = null
let watchersRegistered = false
let initPromise: Promise<void> | null = null

const globalScope = effectScope(true)


export interface ThemeSyncReturn {
  isSyncing: typeof isSyncing
  isInitialized: typeof isInitialized
  themeOverrideActive: typeof themeOverrideActive
  syncWithBackend: (forcedTheme?: ThemeString, endSession?: boolean) => Promise<void>
  handleSessionEnd: () => Promise<void>
  checkUserSettings: () => Promise<void>
  resetSyncState: () => void
}

export const useThemeSync = (): ThemeSyncReturn => {
  const systemStore = useSystemStore()
  const userParameterStore = useUserParameterStore()
  const authStore = useAuthStore()

  const isClientMode = computed(() => !!authStore.clientMode.contract)

  const getUserId = () => authStore.userId || systemStore.options.userId || null
  const getTenantId = () => authStore.getActiveTentant || ''

  const persistence = createPersistence(getUserId, getTenantId)
  const suppressor = createWatcherSuppressor()

  const applyTheme = (value: number) => {
    if (!themeOverrideActive.value) {
      systemStore.setTheme(value)
    }
  }

  const buildPayload = (forcedTheme?: ThemeString) => ({
    ...(systemStore.options.id && { id: systemStore.options.id }),
    userId: getUserId(),
    theme: forcedTheme ?? themeUtils.toString(systemStore.options.dark),
    language: systemStore.options.lang.toUpperCase(),
    volume: systemStore.options.volume,
    autoPlay: systemStore.options.autoplay
  })

  const syncWithBackend = async (forcedTheme?: ThemeString, endSession = false) => {
    if (isClientMode.value || isSyncing.value || !getUserId()) return
    if (!hasPendingChanges.value && !forcedTheme && !endSession) return

    isSyncing.value = true
    try {
      const payload = buildPayload(forcedTheme)

      if (!systemStore.options.id) {
        const result = await userParameterStore.create(payload) as UserParameterType | undefined
        if (result?.id) systemStore.options.id = result.id
      } else {
        await userParameterStore.update(payload)
      }

      if (endSession) {
        await _handlePostSessionEnd()
      } else {
        systemStore.options.lastSyncTimestamp = Date.now()
        systemStore.options.lastUpdate = Date.now()
        hasPendingChanges.value = false
        persistence.save(_buildPersistedState())
      }
    } catch (error) {
      console.error('[ThemeSync] Erro na sincronização:', error)
    } finally {
      isSyncing.value = false
    }
  }

  const _handlePostSessionEnd = async () => {
    persistence.clear()
    suppressor.suppress()

    Object.assign(systemStore.options, {
      id: '',
      userId: undefined,
      dark: 1,
      lang: 'pt',
      volume: 100,
      autoplay: true,
      lastUpdate: null,
      lastSyncTimestamp: null,
      forceSync: false
    })

    hasPendingChanges.value = false
    await nextTick()
  }

  const applyRemotePreferences = (data: UserParameterType) => {
    suppressor.suppress()

    if (data.id) systemStore.options.id = data.id
    if (data.userId) systemStore.options.userId = data.userId

    if (data.language) {
      const lang = data.language.toLowerCase()
      systemStore.options.lang = lang
      systemStore.setLanguage(lang)
    }

    if (data.volume != null) {
      systemStore.options.volume = data.volume
      systemStore.setVolume(data.volume)
    }

    if (data.autoPlay != null) systemStore.options.autoplay = data.autoPlay

    if (data.theme && !themeOverrideActive.value) {
      applyTheme(themeUtils.toValue(data.theme))
    }
  }

  const _buildPersistedState = (): Omit<PersistedState, 'userId' | 'tenantId'> => ({
    id: systemStore.options.id,
    lastUpdate: systemStore.options.lastUpdate,
    lastSyncTimestamp: systemStore.options.lastSyncTimestamp,
    dark: systemStore.options.dark,
    lang: systemStore.options.lang,
    volume: systemStore.options.volume,
    autoplay: systemStore.options.autoplay,
    hasPendingChanges: hasPendingChanges.value
  })

  /**
   * Inicializa a sincronização (somente GET, nunca POST/PUT).
   * Protegido contra chamadas concorrentes via promise singleton.
   */
  const initializeSync = async () => {
    if (!getUserId() || isClientMode.value || isInitialized.value) return
    if (initPromise) return initPromise

    initPromise = (async () => {
      try {
        const persisted = persistence.load()
        const hasFreshCache = !!persisted?.lastSyncTimestamp && !systemStore.options.forceSync

        if (hasFreshCache) {
          if (persisted.id && !systemStore.options.id) {
            systemStore.options.id = persisted.id
          }
          hasPendingChanges.value = persisted.hasPendingChanges
        } else {
          hasPendingChanges.value = false
          suppressor.suppress()

          if (persisted?.id && !systemStore.options.id) {
            systemStore.options.id = persisted.id
          }

          const remote = await userParameterStore.getUserParameter()

          if (remote) {
            applyRemotePreferences(remote)
            systemStore.options.lastSyncTimestamp = Date.now()
            hasPendingChanges.value = false
            persistence.save(_buildPersistedState())
          }

          systemStore.options.forceSync = false
        }

        startTimer()
        isInitialized.value = true
      } catch (error) {
        console.error('[ThemeSync] Erro na inicialização:', error)
      } finally {
        initPromise = null
      }
    })()

    return initPromise
  }

  const startTimer = () => {
    stopTimer()
    if (isClientMode.value) return

    syncTimer = setInterval(() => {
      if (getUserId() && !isSyncing.value) syncWithBackend()
    }, SYNC_INTERVAL_MS)
  }

  const stopTimer = () => {
    if (syncTimer) {
      clearInterval(syncTimer)
      syncTimer = null
    }
  }

  if (!watchersRegistered) {
    watchersRegistered = true

    globalScope.run(() => {
      const trackedPrefs = computed(() => ({
        dark: systemStore.options.dark,
        lang: systemStore.options.lang,
        volume: systemStore.options.volume,
        autoplay: systemStore.options.autoplay
      }))

      watchDebounced(
        trackedPrefs,
        (newVal, oldVal) => {
          if (suppressor.isSuppressed()) return
          if (isClientMode.value || isSyncing.value || !isInitialized.value) return
          if (!oldVal) return

          const changed =
            newVal.dark !== oldVal.dark ||
            newVal.lang !== oldVal.lang ||
            newVal.volume !== oldVal.volume ||
            newVal.autoplay !== oldVal.autoplay

          if (!changed) return

          systemStore.options.lastUpdate = Date.now()
          hasPendingChanges.value = true
          persistence.save(_buildPersistedState())
        },
        { debounce: PREFS_DEBOUNCE_MS }
      )

      watch(
        () => authStore.userId,
        async (newUserId) => {
          if (newUserId && !isClientMode.value) {
            if (!isInitialized.value) await initializeSync()
          } else if (!newUserId) {
            isInitialized.value = false
            stopTimer()
          }
        },
        { immediate: true }
      )

      watch(
        () => authStore.getTenantCurrentPartner,
        async (newTenant, oldTenant) => {
          if (!newTenant || newTenant === oldTenant || isClientMode.value) return

          if (hasPendingChanges.value) {
            await syncWithBackend()
            persistence.clear()
          }

          systemStore.options.id = ''
          systemStore.options.forceSync = true
          isInitialized.value = false
          stopTimer()

          await initializeSync()
        }
      )

      // Override de tema pelo gestor
      watch(
        () => systemStore.clientSettings?.settings?.theme,
        (config) => {
          if (!config || isClientMode.value) return

          const isAdminOverride =
            config.defaultTheme === 'Claro' || config.defaultTheme === 'Escuro'

          themeOverrideActive.value = isAdminOverride

          if (isAdminOverride) {
            suppressor.suppress()
            systemStore.setTheme(THEME_MAP[config.defaultTheme as ThemeString])
          }
        },
        { immediate: true }
      )
    })
  }

  const resetSyncState = () => {
    systemStore.options.id = ''
    isInitialized.value = false
    hasPendingChanges.value = false
    persistence.clear()
    stopTimer()
  }

  return {
    isSyncing,
    isInitialized,
    themeOverrideActive,
    syncWithBackend,
    handleSessionEnd: () => syncWithBackend(undefined, true),
    checkUserSettings: initializeSync,
    resetSyncState
  }
}